import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionPaymentClaim, 
  ConstructionPaymentItem,
  ConstructionPaymentRecord,
  PaymentSummary
} from '../types/construction-payment.types';

export const ConstructionPaymentsService = {
  /**
   * Danh sách đợt thanh toán
   */
  async getClaims(companyId: string, projectId?: string): Promise<ConstructionPaymentClaim[]> {
    let query = supabase
      .from('construction_payment_claims')
      .select(`
        *,
        project:projects(id, name),
        submitter:user_profiles!submitted_by(id, full_name, avatar_url:avatar_path)
      `)
      .eq('company_id', companyId);

    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data as ConstructionPaymentClaim[];
  },

  /**
   * Chi tiết đợt thanh toán kèm các hạng mục
   */
  async getClaimDetail(id: string): Promise<ConstructionPaymentClaim> {
    const { data, error } = await supabase
      .from('construction_payment_claims')
      .select(`
        *,
        project:projects(id, name),
        submitter:user_profiles!submitted_by(id, full_name, avatar_url:avatar_path),
        items:construction_payment_items(
          *,
          boq_item:construction_boq_items(id, item_name, item_code)
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionPaymentClaim;
  },

  /**
   * Tổng hợp thanh toán dự án
   */
  async getSummary(companyId: string, projectId?: string): Promise<PaymentSummary> {
    let query = supabase
      .from('construction_payment_claims')
      .select('net_amount, retention_amount, status_code')
      .eq('company_id', companyId);

    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const totalClaimed = data
      .filter(c => ['approved', 'partially_paid', 'paid'].includes(c.status_code))
      .reduce((sum, c) => sum + (c.net_amount || 0), 0);

    const retentionHeld = data
      .filter(c => ['approved', 'partially_paid', 'paid'].includes(c.status_code))
      .reduce((sum, c) => sum + (c.retention_amount || 0), 0);

    // Ở đây có thể lấy thêm tổng giá trị hợp đồng từ bảng contracts
    return {
      totalContractValue: 0, // Placeholder
      totalClaimed,
      totalPaid: 0, // Cần query từ bảng payment_records
      remainingBalance: 0,
      retentionHeld,
    };
  },

  /**
   * Cập nhật trạng thái thanh toán
   */
  async updateClaimStatus(id: string, status: string, userId: string): Promise<void> {
    const updateData: any = { status_code: status };
    
    if (status === 'approved') {
      updateData.approved_by = userId;
      updateData.approved_at = new Date().toISOString();
    } else if (status === 'submitted') {
      updateData.submitted_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('construction_payment_claims')
      .update(updateData)
      .eq('id', id);

    if (error) throw error;
  }
};
